import { InnerBox } from "@/components/elements/InnerBox";
import { Box, Stack, styled, Typography } from "@mui/material";
import { black } from "@/theme/colors";
import { CaseBaseInfoForm } from "@/features/case/components/CaseBasicInfo/CaseBaseInfoForm";
import React, { useState, useRef, useEffect } from "react";
import { CASE_BASIC_INFO_TAB, CASE_BASIC_INFO_TAB_LABEL } from "@/features/case/constants/caseBasicInfoTab";
import { CaseJobInfo } from "@/features/case/components/CaseBasicInfo/CaseJobInfo";
import { CaseRequestInfo } from "@/features/case/components/CaseBasicInfo/CaseRequestInfo";
import { FCC } from "@/types/common";
import { TGetJobDto } from "@/api/services/main/job/dtos/get-job.dto";
import { Button } from "@/components/elements/Button";
import { IconSave } from "@/assets/icons";

export type CaseBasicInfoProps = {
  jobInfo: TGetJobDto;
  disabled?: boolean;
  isCompleted?: boolean;
  isPersonInChargeLocked?: boolean;
  onSave?: () => void;
  isSubmitting?: boolean;
  hasChanges?: boolean;
};

const TAB_ITEMS = [
  { value: CASE_BASIC_INFO_TAB.BASIC_FORM, label: CASE_BASIC_INFO_TAB_LABEL[CASE_BASIC_INFO_TAB.BASIC_FORM] },
  { value: CASE_BASIC_INFO_TAB.JOB_INFO, label: CASE_BASIC_INFO_TAB_LABEL[CASE_BASIC_INFO_TAB.JOB_INFO] },
  { value: CASE_BASIC_INFO_TAB.REQUEST, label: CASE_BASIC_INFO_TAB_LABEL[CASE_BASIC_INFO_TAB.REQUEST] },
];

export const CaseBasicInfo: FCC<CaseBasicInfoProps> = ({
  jobInfo,
  disabled = false,
  isCompleted = false,
  isPersonInChargeLocked = false,
  onSave,
  isSubmitting = false,
  hasChanges = false,
}) => {
  const [actionTab, setActionTab] = useState(CASE_BASIC_INFO_TAB.BASIC_FORM);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const activeIndex = TAB_ITEMS.findIndex((item) => item.value === actionTab);
    const activeTab = tabRefs.current[activeIndex];
    const container = containerRef.current;

    if (activeTab && container) {
      const containerRect = container.getBoundingClientRect();
      const tabRect = activeTab.getBoundingClientRect();
      setIndicatorStyle({
        left: tabRect.left - containerRect.left,
        width: tabRect.width,
      });
    }
  }, [actionTab]);

  const handleTabClick = (value: number) => {
    setActionTab(value);
  };

  const getTitle = () => {
    return CASE_BASIC_INFO_TAB_LABEL[actionTab];
  };

  // Show save button only in BASIC_FORM tab and when onSave is provided (edit mode)
  const showSaveButton = actionTab === CASE_BASIC_INFO_TAB.BASIC_FORM && onSave && !isCompleted;

  return (
    <InnerBox mt={4} showDivider={false}>
      <Stack direction="row" justifyContent="space-between" mb={4}>
        <Typography variant="sub16Bold">{getTitle()}</Typography>
        <SlidingTabContainer ref={containerRef}>
          <SlidingIndicator style={{ left: indicatorStyle.left, width: indicatorStyle.width }} />
          {TAB_ITEMS.map((item, index) => (
            <TabButton
              key={item.value}
              type="button"
              ref={(el: HTMLButtonElement | null) => {
                tabRefs.current[index] = el;
              }}
              onClick={() => handleTabClick(item.value)}
              $isActive={actionTab === item.value}
            >
              <Typography variant={actionTab === item.value ? "body14Semi" : "body14"}>{item.label}</Typography>
            </TabButton>
          ))}
        </SlidingTabContainer>
      </Stack>
      {actionTab === CASE_BASIC_INFO_TAB.BASIC_FORM && (
        <>
          <CaseBaseInfoForm disabled={disabled} isCompleted={isCompleted} isPersonInChargeLocked={isPersonInChargeLocked} />
          {showSaveButton && (
            <Box display="flex" justifyContent="flex-end" mt={3}>
              <Button
                variant="primary"
                size="sm"
                startIcon={<IconSave width={16} height={16} />}
                onClick={onSave}
                disabled={disabled || isSubmitting || !hasChanges}
              >
                保存
              </Button>
            </Box>
          )}
        </>
      )}
      {actionTab === CASE_BASIC_INFO_TAB.JOB_INFO && <CaseJobInfo jobInfo={jobInfo} />}
      {actionTab === CASE_BASIC_INFO_TAB.REQUEST && <CaseRequestInfo jobInfo={jobInfo} />}
    </InnerBox>
  );
};

const SlidingTabContainer = styled(Box)`
  position: relative;
  display: flex;
  background-color: ${black[99]};
  border-radius: 24px;
  padding: 4px;
`;

const SlidingIndicator = styled(Box)`
  position: absolute;
  top: 4px;
  height: calc(100% - 8px);
  background-color: white;
  border-radius: 20px;
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.1);
  transition:
    left 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 0;
`;

const TabButton = styled("button")<{ $isActive: boolean }>`
  position: relative;
  z-index: 1;
  border: none;
  border-radius: 20px;
  height: 36px;
  min-width: 90px;
  padding: 8px 16px;
  background-color: transparent;
  color: ${({ $isActive }) => ($isActive ? black[30] : black[70])};
  cursor: pointer;
  transition: color 0.2s ease;
  font-family: inherit;

  &:hover {
    background-color: transparent;
  }

  &:focus {
    outline: none;
  }
`;
